import { UseQueryResult } from "@tanstack/react-query";
import { ArrowRightOutline24Icon } from "@vygruppen/spor-icon-react";
import { CountryCode } from "@vygruppen/vy-train-map";
import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import { TrainSummary } from "features/CenterContent/VehicleDetails/TrainDetails/TrainComposition/TrainSummary/TrainSummary";
import { VehicleComposition } from "features/CenterContent/VehicleDetails/TrainDetails/TrainComposition/VehicleComposition";
import { useTrainComposition } from "features/CenterContent/VehicleDetails/TrainDetails/TrainComposition/useTrainComposition";
import { combineStatus } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/getInfoMessages";
import { TrainRouteResponse } from "features/CenterContent/VehicleDetails/TrainDetails/useTrainRoute";
import { useTranslation } from "react-i18next";
import { Flex } from "shared/components/Spor";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import { Text } from "shared/components/typography/TitleStyles";
import styled from "styled-components";

const VehicleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;

const OrientationArrow = styled(ArrowRightOutline24Icon)`
  path {
    fill: ${({ theme }) => theme.colorTextMain};
  }
  margin-top: 30px;
  margin-left: 6px;
  flex-shrink: 0;
`;

type TrainCompositionProps = {
  trainRoute: UseQueryResult<TrainRouteResponse>;
  nominalDate: string | undefined;
  trainId: string | undefined;
  countryCode: CountryCode | undefined;
};

const TrainComposition = ({
  nominalDate,
  trainId,
  countryCode,
  trainRoute,
}: TrainCompositionProps) => {
  const { status: trainCompositionStatus, data } = useTrainComposition({
    nominalDate,
    identifier: trainId,
    countryCode,
  });

  const { t } = useTranslation();

  const status = combineStatus(trainCompositionStatus, trainRoute.status);

  if (status === "pending") {
    return <SkeletonLoader skeletonType="trainComposition" />;
  }

  const hasNoCompositionData = data?.trainComposition.every(
    (set) => set.compositionPositions.length === 0,
  );

  return (
    <>
      {trainRoute.data && <TrainSummary train={trainRoute.data} />}
      <Flex flexGrow="1" justifyContent="center">
        {data && hasNoCompositionData ? (
          <NoDataMessage
            reason={t("Fant ingen informasjon om togsammensetning.")}
          />
        ) : (
          data?.trainComposition
            ?.sort((a, b) => +b.position - +a.position)
            .map((vehicleSet) => (
              <VehicleWrapper
                key={`${vehicleSet.setId}_${vehicleSet.position}_${vehicleSet.setTypeId}`}
              >
                <Text bold>{vehicleSet.setId}</Text>
                {vehicleSet.compositionPositions.length > 0 && (
                  <VehicleComposition
                    compositions={vehicleSet.compositionPositions}
                  />
                )}
              </VehicleWrapper>
            ))
        )}
        <OrientationArrow />
      </Flex>
    </>
  );
};

export default withErrorBoundary(TrainComposition);
